import {
  FLOOR_UNIT_EPSILON,
  calculateRecommendedGalleys,
  floorUnitsPerSeat,
  getEffectivePitchInches,
  getFloorUnitsUsed,
  getTotalSeats,
} from '../../../domain/economics';
import {
  SEAT_CLASS_SPECS,
  type AircraftType,
  type CabinConfiguration,
  type SeatClass,
} from '../../../domain/models';
import { seatsOfClass, withSeatsOfClass } from './cabinFormat';
import type { QuickPresetDefinition, QuickPresetId } from './cabinEditorTypes';

/**
 * Quick presets are pure functions of the aircraft type: seat counts are sized
 * from the type's floor budget and certified seat limit, so every preset is a
 * legal layout for any type (provided the type can seat at least one passenger).
 */

export const QUICK_PRESETS: readonly QuickPresetDefinition[] = [
  {
    id: 'HIGH_DENSITY_LOW_COST',
    label: 'High Density Low-Cost',
    description: 'All-economy at minimum pitch, no amenities. Most seats, lowest comfort.',
  },
  {
    id: 'STANDARD_TWO_CLASS',
    label: 'Standard 2-Class',
    description: 'Economy at 31″ plus a business cabin, with Wi-Fi and in-seat power.',
  },
  {
    id: 'ULTRA_LUXURY_LONG_HAUL',
    label: 'Ultra-Luxury Long-Haul',
    description: 'First, lie-flat business and premium economy; a small economy cabin at 34″.',
  },
];

/** Share of the floor budget given to each premium class per preset. */
const STANDARD_BUSINESS_SHARE = 0.2;
const LUXURY_FIRST_SHARE = 0.3;
const LUXURY_BUSINESS_SHARE = 0.4;
const LUXURY_PREMIUM_ECONOMY_SHARE = 0.1;

/** Classes are trimmed in this order when a layout overshoots a limit. */
const TRIM_ORDER: readonly SeatClass[] = ['ECONOMY', 'PREMIUM_ECONOMY', 'BUSINESS', 'FIRST'];

const EMPTY_CABIN: CabinConfiguration = {
  economySeats: 0,
  economyPitchInches: SEAT_CLASS_SPECS.ECONOMY.defaultPitchInches,
  premiumEconomySeats: 0,
  businessSeats: 0,
  businessPitchInches: SEAT_CLASS_SPECS.BUSINESS.defaultPitchInches,
  firstSeats: 0,
  galleyCount: 0,
  wifiEquipped: false,
  inSeatPower: false,
};

/** Whole seats of `perSeatUnits` that fit in `units` floor units. */
function seatsThatFit(units: number, perSeatUnits: number): number {
  if (!(units > 0) || !(perSeatUnits > 0)) {
    return 0;
  }
  return Math.max(0, Math.floor((units + FLOOR_UNIT_EPSILON) / perSeatUnits));
}

/** Floor units not yet used by `config`, never negative. */
function remainingUnits(type: AircraftType, config: CabinConfiguration): number {
  return Math.max(0, type.cabinFloorUnits - getFloorUnitsUsed(config));
}

/** Seats still allowed by the certified limit. */
function remainingCertified(type: AircraftType, config: CabinConfiguration): number {
  return Math.max(0, type.maxCertifiedSeats - getTotalSeats(config));
}

/**
 * Adds up to `wanted` seats of a class, limited by `unitsAvailable` floor
 * units and by the certified seat limit.
 */
function addSeats(
  type: AircraftType,
  config: CabinConfiguration,
  seatClass: SeatClass,
  unitsAvailable: number
): CabinConfiguration {
  const perSeat = floorUnitsPerSeat(seatClass, getEffectivePitchInches(seatClass, config));
  const byFloor = seatsThatFit(Math.min(unitsAvailable, remainingUnits(type, config)), perSeat);
  const seats = Math.min(byFloor, remainingCertified(type, config));
  return withSeatsOfClass(config, seatClass, seatsOfClass(config, seatClass) + seats);
}

/**
 * Safety net: removes seats (economy first) until the layout respects both the
 * floor budget and the certified seat limit.
 */
function trimToLimits(type: AircraftType, config: CabinConfiguration): CabinConfiguration {
  let trimmed = config;
  for (const seatClass of TRIM_ORDER) {
    const excessSeats = getTotalSeats(trimmed) - type.maxCertifiedSeats;
    const excessUnits = getFloorUnitsUsed(trimmed) - type.cabinFloorUnits;
    if (excessSeats <= 0 && excessUnits <= FLOOR_UNIT_EPSILON) {
      break;
    }
    const perSeat = floorUnitsPerSeat(seatClass, getEffectivePitchInches(seatClass, trimmed));
    const bySeatLimit = Math.max(0, excessSeats);
    const byFloorLimit =
      excessUnits > FLOOR_UNIT_EPSILON ? Math.ceil((excessUnits - FLOOR_UNIT_EPSILON) / perSeat) : 0;
    const current = seatsOfClass(trimmed, seatClass);
    const reduction = Math.min(current, Math.max(bySeatLimit, byFloorLimit));
    trimmed = withSeatsOfClass(trimmed, seatClass, current - reduction);
  }
  return trimmed;
}

/** Final step for every preset: legal limits, then the recommended galleys. */
function finalize(type: AircraftType, config: CabinConfiguration): CabinConfiguration {
  const legal = trimToLimits(type, config);
  return { ...legal, galleyCount: calculateRecommendedGalleys(legal) };
}

function buildHighDensity(type: AircraftType): CabinConfiguration {
  const base: CabinConfiguration = {
    ...EMPTY_CABIN,
    economyPitchInches: SEAT_CLASS_SPECS.ECONOMY.minPitchInches,
  };
  return finalize(type, addSeats(type, base, 'ECONOMY', type.cabinFloorUnits));
}

function buildStandardTwoClass(type: AircraftType): CabinConfiguration {
  const base: CabinConfiguration = {
    ...EMPTY_CABIN,
    wifiEquipped: true,
    inSeatPower: true,
  };
  const withBusiness = addSeats(type, base, 'BUSINESS', type.cabinFloorUnits * STANDARD_BUSINESS_SHARE);
  const withEconomy = addSeats(type, withBusiness, 'ECONOMY', type.cabinFloorUnits);
  return finalize(type, withEconomy);
}

function buildUltraLuxury(type: AircraftType): CabinConfiguration {
  const base: CabinConfiguration = {
    ...EMPTY_CABIN,
    economyPitchInches: SEAT_CLASS_SPECS.ECONOMY.maxPitchInches,
    businessPitchInches: SEAT_CLASS_SPECS.BUSINESS.maxPitchInches,
    wifiEquipped: true,
    inSeatPower: true,
  };
  const withFirst = addSeats(type, base, 'FIRST', type.cabinFloorUnits * LUXURY_FIRST_SHARE);
  const withBusiness = addSeats(type, withFirst, 'BUSINESS', type.cabinFloorUnits * LUXURY_BUSINESS_SHARE);
  const withPremium = addSeats(
    type,
    withBusiness,
    'PREMIUM_ECONOMY',
    type.cabinFloorUnits * LUXURY_PREMIUM_ECONOMY_SHARE
  );
  const withEconomy = addSeats(type, withPremium, 'ECONOMY', type.cabinFloorUnits);
  return finalize(type, withEconomy);
}

/** Builds the layout for a quick preset on the given aircraft type. */
export function buildQuickPreset(id: QuickPresetId, type: AircraftType): CabinConfiguration {
  switch (id) {
    case 'HIGH_DENSITY_LOW_COST':
      return buildHighDensity(type);
    case 'STANDARD_TWO_CLASS':
      return buildStandardTwoClass(type);
    case 'ULTRA_LUXURY_LONG_HAUL':
      return buildUltraLuxury(type);
  }
}
